import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '../../../../environments/environment';
import { NEW_REGISTRATION_BASE } from '../../../features-modules/new-registration/new-registration.paths';
import { HttpRes } from '../../../models/common/http.model';
import { CommonServiceService } from '../common-service/common-service.service';
import { DashboardService } from '../dashboard-service/dashboard.service';
import { EncryptDecryptService } from '../EncrptyDecrypt/encrypt-decrypt.service';
import { RoleServiceService } from '../role-service/role-service.service';
import { TemplateService } from '../template/template.service';
import { clearSentryUser } from '../../monitoring/sentry/sentry-user.util';

/** Identifies a profile-menu action; the service maps it to real navigation. */
export type UserMenuActionId =
  | 'login'
  | 'admin'
  | 'registration'
  | 'invoice_history'
  | 'request_management'
  | 'my_registration'
  | 'new_registration_history'
  | 'order_history'
  | 'profile'
  | 'logout';

/** One rendered row in the profile menu. */
export interface UserMenuItem {
  readonly id: UserMenuActionId;
  /** i18n key for the row label. */
  readonly labelKey: string;
  /** Destructive styling (logout). */
  readonly danger?: boolean;
}

/** Shape of one eligible-role row used when priming registration session state. */
interface EligibleRoleRow {
  readonly role_id: number;
}

/**
 * Profile / account menu shared by the header (template 0) and the sidebar rail
 * (template 1).
 *
 * Holds the menu model and every action it can perform — role resolution,
 * navigation targets and logout teardown — so both layouts drive identical
 * behaviour from one implementation.
 */
@Injectable({ providedIn: 'root' })
export class UserMenuService {
  private readonly router = inject(Router);
  private readonly commonService = inject(CommonServiceService);
  private readonly roleService = inject(RoleServiceService);
  private readonly encryptDecryptService = inject(EncryptDecryptService);
  private readonly templateService = inject(TemplateService);
  private readonly dashboardService = inject(DashboardService);
  private readonly translate = inject(TranslateService);

  /**
   * True when the signed-in user's JWT role is "player".
   *
   * Read from the token on each call rather than cached, so it stays correct
   * after a login/logout without needing a subscription.
   */
  get isPlayer(): boolean {
    const token = this.readToken();
    if (!token) {
      return false;
    }
    try {
      const payload: unknown = JSON.parse(atob(token.split('.')[1]));
      if (typeof payload !== 'object' || payload === null) {
        return false;
      }
      const roleName = (payload as { role_name?: unknown }).role_name;
      return typeof roleName === 'string' && roleName.toLowerCase() === 'player';
    } catch {
      // Malformed JWT — treat as a non-player rather than throwing.
      return false;
    }
  }

  /**
   * True when the stored (encrypted) `role_id` equals the stored
   * `player_role_id`.
   *
   * Deliberately a *second*, different player check: the legacy header used
   * this comparison for the "Profile" destination while using the JWT
   * {@link isPlayer} for "My Registration". Both are preserved verbatim so
   * neither call site changes behaviour.
   */
  get isPlayerByRoleId(): boolean {
    if (!globalThis.window?.localStorage) {
      return false;
    }
    try {
      const roleId = this.readRoleId();
      const playerRoleId = localStorage.getItem('player_role_id');
      return (
        roleId !== null &&
        !!playerRoleId &&
        roleId.toString() === playerRoleId.toString()
      );
    } catch {
      return false;
    }
  }

  /** True when a session token is present. */
  get isLoggedIn(): boolean {
    return this.readToken() !== null;
  }

  /** Invoice History is hidden for the Swiss tenants. */
  get showInvoiceHistory(): boolean {
    return (
      environment.tenant_Name !== 'switz' &&
      environment.tenant_Name !== 'test_switz'
    );
  }

  /**
   * True when the stored role is the admin role (`role_id === 1`).
   *
   * Gates the "Switch to Admin" entry, matching the header's `is_admin` check.
   */
  get isAdmin(): boolean {
    return this.readRoleId() === 1;
  }

  /**
   * Builds the profile menu for the current user.
   *
   * Mirrors the template-0 header's `*ngIf` gating exactly: signed-out users get
   * a Login entry, "Switch to Admin" needs the admin role, and Invoice History
   * is hidden on the Swiss tenants.
   *
   * @returns Menu rows in display order.
   */
  buildMenu(): readonly UserMenuItem[] {
    if (!this.isLoggedIn) {
      return [{ id: 'login', labelKey: 'login' }];
    }

    const items: UserMenuItem[] = [];

    if (this.isAdmin) {
      items.push({ id: 'admin', labelKey: 'switch_to_admin' });
    }

    items.push({ id: 'registration', labelKey: 'registration' });

    if (this.showInvoiceHistory) {
      items.push({ id: 'invoice_history', labelKey: 'invoice_history' });
    }

    items.push(
      { id: 'request_management', labelKey: 'request_management' },
      { id: 'my_registration', labelKey: 'my_registration' },
      { id: 'new_registration_history', labelKey: 'new_registration' },
      { id: 'profile', labelKey: 'my_profile' },
      { id: 'logout', labelKey: 'logout', danger: true },
    );

    return items;
  }

  /**
   * Performs a profile-menu action.
   *
   * @param id Action the user selected.
   */
  run(id: UserMenuActionId): void {
    switch (id) {
      case 'login':
        this.commonService.login_subject.next(true);
        this.commonService.is_login_success.next(false);
        break;
      case 'admin':
        this.openAdminConsole();
        break;
      case 'registration':
        void this.router.navigate(['/registration']);
        break;
      case 'invoice_history':
        void this.router.navigate(['order-history/invoice']);
        break;
      case 'request_management':
        void this.router.navigate(['events/requests']);
        break;
      case 'my_registration':
        this.openMyRegistration();
        break;
      case 'new_registration_history':
        void this.router.navigate([NEW_REGISTRATION_BASE, 'history']);
        break;
      case 'order_history':
        void this.router.navigate(['order-history/']);
        this.primeEligibleRoles();
        break;
      case 'profile':
        this.openProfile();
        break;
      case 'logout':
        this.logout();
        break;
    }
  }

  /** Opens the admin console in a new tab, carrying the session token. */
  private openAdminConsole(): void {
    if (globalThis.window === undefined) {
      return;
    }
    const token = localStorage.getItem('webToken') ?? '';
    globalThis.window.open(`${environment.adminUrl}${token}`, '_blank');
  }

  /**
   * Players land on their own registrations; everyone else on order history,
   * which additionally needs eligible-role session state primed.
   */
  private openMyRegistration(): void {
    if (this.isPlayer) {
      void this.router.navigate(['registration', 'my-registration']);
      return;
    }
    void this.router.navigate(['order-history']);
    this.primeEligibleRoles();
  }

  /**
   * Routes to the dashboard or the classic profile page.
   *
   * Uses the stored-role-id check (not the JWT one) to match the legacy
   * header's "Profile" behaviour exactly.
   */
  private openProfile(): void {
    if (this.isPlayerByRoleId && environment.show_player_dashboad === true) {
      void this.router.navigate(['my-profile-dashboard']);
      return;
    }
    void this.router.navigate(['my-space/my-profile/']);
  }

  /**
   * Clears the session and returns to home.
   *
   * Template 0 re-opens the login panel straight away; template 1 confirms with
   * a toast instead, because its sign-in is a centred modal that would block the
   * page the user just chose to browse signed-out.
   *
   * The persisted theme is deliberately restored after the storage wipe so a
   * signed-out user keeps their light/dark preference.
   */
  private logout(): void {
    if (globalThis.window?.localStorage) {
      const themeKey = localStorage.getItem('theme');

      localStorage.removeItem('token');
      localStorage.removeItem('webToken');
      sessionStorage.removeItem('webToken');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('role_id');
      localStorage.clear();
      sessionStorage.clear();

      if (themeKey) {
        localStorage.setItem('theme', themeKey);
      }
    }

    clearSentryUser();
    void this.router.navigate(['/home']);
    const isTemplate1 = this.templateService.isTemplate1;
    this.commonService.login_subject.next(!isTemplate1);
    this.commonService.is_login_success.next(false);
    if (isTemplate1) {
      this.dashboardService.toast(
        this.translate.instant('logout_success'),
        'success',
      );
    }
  }

  /**
   * Stores the current user's eligible registration roles in session storage,
   * which the order-history screens read on load.
   */
  private primeEligibleRoles(): void {
    this.roleService.eligibleRolesForRegistration(6, 5).subscribe({
      next: (res: HttpRes) => {
        if (res.code !== 200 || !res.data) {
          return;
        }
        if (!globalThis.window?.localStorage) {
          return;
        }
        const roleId = this.readRoleId();
        if (roleId === null) {
          return;
        }
        const rows: unknown = res.data.data;
        if (!Array.isArray(rows)) {
          return;
        }
        const mine = (rows as EligibleRoleRow[]).filter(
          (row) => row.role_id === roleId,
        );
        sessionStorage.setItem('data', JSON.stringify(mine));
        this.commonService.setApiResponse(mine);
      },
      error: (error: unknown) => {
        console.error('Failed to load eligible roles', error);
      },
    });
  }

  /**
   * Decrypts the stored role id.
   *
   * @returns Role id, or `null` when absent or unreadable.
   */
  private readRoleId(): number | null {
    try {
      const raw = localStorage.getItem('role_id');
      if (!raw) {
        return null;
      }
      const decrypted: unknown = JSON.parse(
        this.encryptDecryptService.decryptUsingAES256(raw),
      );
      return typeof decrypted === 'number' ? decrypted : null;
    } catch {
      return null;
    }
  }

  /** Session token from either storage key used across the app. */
  private readToken(): string | null {
    if (!globalThis.window?.localStorage) {
      return null;
    }
    return localStorage.getItem('webToken') || localStorage.getItem('token');
  }
}
